import {
  useRef,
  useEffect,
  useState,
  useImperativeHandle,
  forwardRef,
} from 'react';

interface ScreenShareProps {
  isSharing: boolean;
  onShareChange: (sharing: boolean) => void;
  onFrameCapture?: (imageData: string) => void;
}

export interface ScreenShareHandle {
  captureFrame: () => void;
}

const ScreenShare = forwardRef<ScreenShareHandle, ScreenShareProps>(
  ({ isSharing, onShareChange, onFrameCapture }, ref) => {
    const videoRef = useRef<HTMLVideoElement>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const streamRef = useRef<MediaStream | null>(null);
    const captureIntervalRef = useRef<number | null>(null);
    const [frameCount, setFrameCount] = useState<number>(0);
    const [lastFrameTime, setLastFrameTime] = useState<string | null>(null);

    useEffect(() => {
      if (isSharing) {
        startScreenShare();
      } else {
        stopScreenShare();
      }

      return () => {
        stopScreenShare();
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isSharing]);

    const startScreenShare = async (): Promise<void> => {
      try {
        console.log('🎬 Requesting screen share permission...');

        if (!videoRef.current) {
          console.error(
            '❌ Video ref is null! Waiting for component to mount...'
          );
          setTimeout(() => {
            if (videoRef.current) {
              startScreenShare();
            } else {
              alert('Video element not found. Please refresh the page.');
              onShareChange(false);
            }
          }, 100);
          return;
        }

        if (!canvasRef.current) {
          console.error(
            '❌ Canvas ref is null! Waiting for component to mount...'
          );
          setTimeout(() => {
            if (canvasRef.current) {
              startScreenShare();
            } else {
              alert('Canvas element not found. Please refresh the page.');
              onShareChange(false);
            }
          }, 100);
          return;
        }

        const stream = await navigator.mediaDevices.getDisplayMedia({
          video: {
            width: { ideal: 1920 },
            height: { ideal: 1080 },
          },
          audio: false,
        } as MediaStreamConstraints);

        console.log('✅ Screen share stream obtained:', {
          id: stream.id,
          active: stream.active,
          videoTracks: stream.getVideoTracks().length,
          trackSettings: stream.getVideoTracks()[0]?.getSettings(),
        });

        streamRef.current = stream;

        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.play().catch((err) => {
            console.error('Error playing video:', err);
          });
        }

        const handleLoadedMetadata = (): void => {
          if (!videoRef.current) {
            console.warn(
              'Video ref is null in handleLoadedMetadata, component may have unmounted'
            );
            return;
          }

          console.log('✅ Video metadata loaded, starting frame capture', {
            width: videoRef.current.videoWidth,
            height: videoRef.current.videoHeight,
            readyState: videoRef.current.readyState,
            srcObject: !!videoRef.current.srcObject,
          });

          if (
            videoRef.current.videoWidth > 0 &&
            videoRef.current.videoHeight > 0
          ) {
            startFrameCapture();
          } else {
            console.warn('Video has invalid dimensions, retrying...');
            setTimeout(() => {
              if (
                videoRef.current &&
                videoRef.current.videoWidth > 0 &&
                videoRef.current.videoHeight > 0
              ) {
                startFrameCapture();
              }
            }, 500);
          }

          if (videoRef.current) {
            videoRef.current.removeEventListener(
              'loadedmetadata',
              handleLoadedMetadata
            );
          }
        };

        const handleLoadedData = (): void => {
          if (!videoRef.current) {
            console.warn(
              'Video ref is null in handleLoadedData, component may have unmounted'
            );
            return;
          }

          console.log('Video data loaded');
          if (
            videoRef.current.videoWidth > 0 &&
            videoRef.current.videoHeight > 0
          ) {
            startFrameCapture();
          }
        };

        if (videoRef.current) {
          videoRef.current.addEventListener(
            'loadedmetadata',
            handleLoadedMetadata
          );
          videoRef.current.addEventListener('loadeddata', handleLoadedData);
        }

        if (videoRef.current && videoRef.current.readyState >= 2) {
          console.log(
            'Video already loaded, starting frame capture immediately'
          );
          if (
            videoRef.current.videoWidth > 0 &&
            videoRef.current.videoHeight > 0
          ) {
            startFrameCapture();
          } else {
            setTimeout(() => {
              if (
                videoRef.current &&
                videoRef.current.videoWidth > 0 &&
                videoRef.current.videoHeight > 0
              ) {
                startFrameCapture();
              }
            }, 300);
          }
        }

        const videoTrack = stream.getVideoTracks()[0];
        if (videoTrack) {
          videoTrack.addEventListener('ended', () => {
            console.log('Screen share ended by user');
            onShareChange(false);
          });
        }
      } catch (error) {
        console.error('❌ Error starting screen share:', error);
        const errorMessage =
          error instanceof Error ? error.message : String(error);
        alert(
          `Failed to start screen sharing: ${errorMessage}\n\nPlease check browser permissions and try again.`
        );
        onShareChange(false);
      }
    };

    const stopScreenShare = (): void => {
      console.log('🛑 Stopping screen share...');

      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => {
          track.stop();
          console.log('Stopped video track:', track.label);
        });
        streamRef.current = null;
      }

      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }

      if (captureIntervalRef.current) {
        clearInterval(captureIntervalRef.current);
        captureIntervalRef.current = null;
      }

      setFrameCount(0);
      setLastFrameTime(null);
    };

    const startFrameCapture = (): void => {
      if (captureIntervalRef.current) {
        clearInterval(captureIntervalRef.current);
        captureIntervalRef.current = null;
      }

      console.log('🎬 Screen share started - capturing initial frame');

      setTimeout(() => {
        if (isSharing && videoRef.current && videoRef.current.videoWidth > 0) {
          console.log('📸 Capturing initial frame after screen share start');
          captureFrame();
        }
      }, 1000);

      captureIntervalRef.current = window.setInterval(() => {
        if (isSharing && videoRef.current && videoRef.current.videoWidth > 0) {
          captureFrame();
        } else {
          if (captureIntervalRef.current) {
            clearInterval(captureIntervalRef.current);
            captureIntervalRef.current = null;
          }
        }
      }, 5000);
    };

    const captureFrameOnDemand = (): void => {
      console.log('📸 Capturing frame on demand (user requested analysis)');
      if (isSharing && videoRef.current && videoRef.current.videoWidth > 0) {
        captureFrame();
      } else {
        console.warn(
          '⚠️ Cannot capture frame - screen share not active or video not ready'
        );
      }
    };

    useImperativeHandle(ref, () => ({
      captureFrame: captureFrameOnDemand,
    }));

    const captureFrame = (): void => {
      if (!videoRef.current) {
        console.warn('⚠️ Video ref not available for frame capture');
        return;
      }

      if (!canvasRef.current) {
        console.warn('⚠️ Canvas ref not available for frame capture');
        return;
      }

      const video = videoRef.current;
      const canvas = canvasRef.current;

      if (!video.videoWidth || !video.videoHeight) {
        console.warn('⚠️ Video not ready yet, skipping frame capture', {
          videoWidth: video.videoWidth,
          videoHeight: video.videoHeight,
          readyState: video.readyState,
          srcObject: !!video.srcObject,
        });
        return;
      }

      if (!video.srcObject) {
        console.warn('⚠️ Video srcObject is null, skipping frame capture');
        return;
      }

      const tracks = (video.srcObject as MediaStream).getVideoTracks();
      if (tracks.length === 0) {
        console.warn('⚠️ No video tracks available');
        return;
      }

      if (tracks[0].readyState !== 'live') {
        console.warn('⚠️ Video track not live, state:', tracks[0].readyState);
        return;
      }

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        console.error('❌ Could not get canvas context');
        return;
      }

      const MAX_DIMENSION = 800;
      let targetWidth = video.videoWidth;
      let targetHeight = video.videoHeight;

      if (targetWidth > MAX_DIMENSION || targetHeight > MAX_DIMENSION) {
        const scale = Math.min(
          MAX_DIMENSION / targetWidth,
          MAX_DIMENSION / targetHeight
        );
        targetWidth = Math.floor(targetWidth * scale);
        targetHeight = Math.floor(targetHeight * scale);
      }

      if (canvas.width !== targetWidth || canvas.height !== targetHeight) {
        canvas.width = targetWidth;
        canvas.height = targetHeight;
        console.log('Canvas resized for compression:', {
          original: { width: video.videoWidth, height: video.videoHeight },
          compressed: { width: canvas.width, height: canvas.height },
        });
      }

      try {
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

        let imageData: string;
        try {
          imageData = canvas.toDataURL('image/jpeg', 0.6).split(',')[1];
        } catch (jpegError) {
          console.warn('JPEG conversion failed, using PNG:', jpegError);
          imageData = canvas.toDataURL('image/png').split(',')[1];
        }

        if (!imageData) {
          console.warn('⚠️ Image data is null');
          return;
        }

        if (imageData.length < 100) {
          console.warn('⚠️ Image data too small, skipping send', {
            dataLength: imageData.length,
          });
          return;
        }

        if (onFrameCapture) {
          console.log('✅ Frame captured successfully, sending to backend', {
            width: canvas.width,
            height: canvas.height,
            dataLength: imageData.length,
            format: imageData.substring(0, 30),
          });
          try {
            onFrameCapture(imageData);
            setFrameCount((prev) => {
              const newCount = prev + 1;
              console.log(`📊 Frame count updated: ${newCount}`);
              return newCount;
            });
            setLastFrameTime(new Date().toLocaleTimeString());
            console.log('✅ Frame sent successfully');
          } catch (error) {
            console.error('❌ Error calling onFrameCapture:', error);
          }
        } else {
          console.error('❌ onFrameCapture callback not available!');
        }
      } catch (error) {
        console.error('❌ Error capturing frame:', error);
        console.error('Error details:', {
          videoWidth: video.videoWidth,
          videoHeight: video.videoHeight,
          canvasWidth: canvas.width,
          canvasHeight: canvas.height,
          errorMessage: error instanceof Error ? error.message : String(error),
        });
      }
    };

    return (
      <div className="bg-white rounded-xl p-4 shadow-lg">
        <div className="flex justify-between items-center mb-4 pb-3 border-b-2 border-slate-200">
          <h3 className="m-0 text-slate-800 text-xl font-semibold">
            Screen Share
          </h3>
          <button
            className={`px-4 py-2 border-none rounded-md text-sm font-semibold cursor-pointer transition-all duration-200 ${
              isSharing
                ? 'bg-red-500 text-white hover:bg-red-600'
                : 'bg-indigo-500 text-white hover:bg-indigo-600'
            }`}
            onClick={() => onShareChange(!isSharing)}
          >
            {isSharing ? '⏹ Stop Sharing' : '🖥️ Start Sharing'}
          </button>
        </div>

        <div className="relative bg-slate-900 rounded-lg overflow-hidden min-h-[300px] flex items-center justify-center">
          <video
            ref={videoRef}
            autoPlay
            muted
            playsInline
            className={`w-full h-full object-contain ${
              isSharing ? 'block' : 'hidden'
            }`}
          />
          <canvas ref={canvasRef} className="hidden" />

          {isSharing ? (
            <div className="absolute inset-0 bg-black/50 flex flex-col items-center justify-center text-white p-4 text-center">
              <p className="text-lg font-semibold mb-2">
                Screen is being shared and analyzed in real-time
              </p>
              <p className="text-sm text-slate-300 mt-2">
                Frames captured: {frameCount}{' '}
                {lastFrameTime && `(last: ${lastFrameTime})`}
              </p>
              {frameCount === 0 && (
                <p className="text-xs text-red-400 mt-1">
                  ⚠️ No frames sent yet. Check console for errors.
                </p>
              )}
            </div>
          ) : (
            <div className="text-center p-8 text-slate-400">
              <p className="text-lg mb-2">
                Click "Start Sharing" to share your screen
              </p>
              <p className="text-sm">
                The AI will analyze your code/design in real-time
              </p>
            </div>
          )}
        </div>
      </div>
    );
  }
);

ScreenShare.displayName = 'ScreenShare';

export default ScreenShare;
